import { useState } from 'react'
import { X } from 'lucide-react'
import type { AdminConfig, AdminConfigCreateRequest, ConfigValueType } from '../../types'
import { useI18n } from '../../i18n'

interface ConfigFormDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: AdminConfigCreateRequest) => Promise<boolean>
  editing?: AdminConfig | null
}

const valueTypes: ConfigValueType[] = ['STRING', 'NUMBER', 'BOOLEAN', 'JSON']

export function ConfigFormDialog({ open, onClose, onSubmit, editing }: ConfigFormDialogProps) {
  const { t } = useI18n()
  const [form, setForm] = useState<AdminConfigCreateRequest>(
    editing
      ? {
          configKey: editing.configKey,
          configValue: editing.configValue,
          configName: editing.configName,
          configGroup: editing.configGroup,
          valueType: editing.valueType,
          description: editing.description,
          sortOrder: editing.sortOrder,
        }
      : {
          configKey: '',
          configValue: '',
          configName: '',
          configGroup: 'default',
          valueType: 'STRING',
          description: '',
          sortOrder: 0,
        }
  )
  const [submitting, setSubmitting] = useState(false)

  if (!open) return null

  const handleSubmit = async () => {
    if (!form.configKey || !form.configValue || !form.configName) return
    setSubmitting(true)
    const ok = await onSubmit(form)
    setSubmitting(false)
    if (ok) onClose()
  }

  const update = (key: keyof AdminConfigCreateRequest, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl border border-black/[0.06] shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-black/[0.04]">
          <h3 className="text-sm font-semibold text-[#1a1a1a]">
            {editing ? t('编辑配置', 'Edit Config') : t('新增配置', 'Create Config')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-[#1a1a1a] transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-[11px] text-gray-400 mb-1">
              configKey <span className="text-red-400">*</span>
            </label>
            <input
              value={form.configKey}
              onChange={(e) => update('configKey', e.target.value)}
              disabled={!!editing}
              className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none disabled:opacity-50"
              placeholder="plan.expire.days"
            />
          </div>

          <div>
            <label className="block text-[11px] text-gray-400 mb-1">
              {t('配置名称', 'Name')} <span className="text-red-400">*</span>
            </label>
            <input
              value={form.configName}
              onChange={(e) => update('configName', e.target.value)}
              className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none"
              placeholder={t('订单过期天数', 'Order expire days')}
            />
          </div>

          <div>
            <label className="block text-[11px] text-gray-400 mb-1">
              configValue <span className="text-red-400">*</span>
            </label>
            <textarea
              value={form.configValue}
              onChange={(e) => update('configValue', e.target.value)}
              rows={3}
              className="w-full text-sm px-3 py-2 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none resize-none"
              placeholder="30"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] text-gray-400 mb-1">valueType</label>
              <select
                value={form.valueType}
                onChange={(e) => update('valueType', e.target.value as ConfigValueType)}
                disabled={!!editing}
                className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none disabled:opacity-50"
              >
                {valueTypes.map((vt) => (
                  <option key={vt} value={vt}>{vt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] text-gray-400 mb-1">configGroup</label>
              <input
                value={form.configGroup}
                onChange={(e) => update('configGroup', e.target.value)}
                disabled={!!editing}
                className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none disabled:opacity-50"
                placeholder="default"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] text-gray-400 mb-1">{t('描述', 'Description')}</label>
            <input
              value={form.description ?? ''}
              onChange={(e) => update('description', e.target.value)}
              className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none"
              placeholder={t('配置说明', 'Config description')}
            />
          </div>

          <div>
            <label className="block text-[11px] text-gray-400 mb-1">sortOrder</label>
            <input
              type="number"
              value={form.sortOrder ?? 0}
              onChange={(e) => update('sortOrder', Number(e.target.value))}
              className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-black/[0.04]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[13px] rounded-xl bg-black/[0.04] text-gray-400 hover:text-[#1a1a1a] transition-all"
          >
            {t('取消', 'Cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !form.configKey || !form.configValue || !form.configName}
            className="px-4 py-2 text-[13px] rounded-xl bg-orange-500 text-white hover:bg-orange-600 transition-all disabled:opacity-50"
          >
            {submitting ? '...' : t('确认', 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}
